import type { CitizenLicense, CitizenSummary, DirectoryLog, PageResult, TagRecord, Warrant } from "@hydeck/mdt-contracts";
import { createEffect, createResource, createSignal, For, Show } from "solid-js";
import { DumpDetail, DumpField } from "../components/DumpDetail";
import { DumpIcon } from "../components/DumpIcon";
import { dumpFilterQuery, DumpList } from "../components/DumpList";
import { RichTextEditor } from "../components/RichTextEditor";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

const fallbackProfile =
  "https://assets.nopixel.net/dev/images/mdt/mdt_no_profile_picture.png";

export function ProfilesPage() {
  const [search, setSearch] = createSignal("");
  const [advanced, setAdvanced] = createSignal<Record<string, string>>({});
  const [selected, setSelected] = createSignal<CitizenSummary | null>(null);
  const [stateId, setStateId] = createSignal(0);
  const [name, setName] = createSignal("");
  const [profilePicture, setProfilePicture] = createSignal("");
  const [summary, setSummary] = createSignal("");
  const [dna, setDna] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [expunging, setExpunging] = createSignal(false);
  const [loadingDetail, setLoadingDetail] = createSignal(false);
  let detailRequest = 0;
  const [tagOptions] = createResource(() =>
    apiRequest<TagRecord[]>("/tags", { token: currentUser()?.accessToken }).catch(() => []),
  );
  const [profiles, { refetch }] = createResource(
    () => [search(), JSON.stringify(advanced())] as const,
    ([query]) =>
    apiRequest<PageResult<CitizenSummary>>("/citizens", {
      token: currentUser()?.accessToken,
      query: { page: 1, limit: 50, search: query, ...dumpFilterQuery(advanced()) },
    }),
  );

  const select = async (citizen: CitizenSummary) => {
    const requestId = ++detailRequest;
    setSelected(citizen);
    setStateId(citizen.id);
    setName(citizen.name);
    setProfilePicture(citizen.profilePicture ?? "");
    setSummary(citizen.summary ?? "");
    setDna(citizen.dna ?? "");
    if (!citizen.id) return;
    setLoadingDetail(true);
    try {
      const detail = await apiRequest<CitizenSummary>(`/citizens/${citizen.id}`, {
        token: currentUser()?.accessToken,
      });
      if (requestId !== detailRequest) return;
      setSelected(detail);
      setName(detail.name);
      setProfilePicture(detail.profilePicture ?? "");
      setSummary(detail.summary ?? "");
      setDna(detail.dna ?? "");
    } catch {
      // Keep the list summary visible when a connector is temporarily offline.
    } finally {
      if (requestId === detailRequest) setLoadingDetail(false);
    }
  };

  const createProfile = () => {
    detailRequest += 1;
    setLoadingDetail(false);
    setSelected({
      id: 0,
      name: "",
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      profilePicture: fallbackProfile,
    });
    setStateId(0);
    setName("");
    setProfilePicture(fallbackProfile);
    setSummary("");
    setDna("");
  };

  const save = async () => {
    const citizen = selected();
    const parts = name().trim().split(/\s+/);
    if (!citizen || parts.length < 2) return;
    setSaving(true);
    try {
      const payload = {
        ...(citizen.id ? {} : { id: stateId() > 0 ? stateId() : undefined }),
        firstName: parts.shift()!,
        lastName: parts.join(" "),
        dateOfBirth: citizen.dateOfBirth || "1970-01-01",
        profilePicture: profilePicture() || null,
        summary: summary(),
        dna: dna(),
      };
      const saved = await apiRequest<CitizenSummary>(
        citizen.id ? `/citizens/${citizen.id}` : "/citizens",
        {
          method: citizen.id ? "PATCH" : "POST",
          token: currentUser()?.accessToken,
          body: payload,
        },
      );
      await select(saved);
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const expunge = async () => {
    const citizen = selected();
    if (!citizen?.id || !window.confirm("Expunge this citizen's criminal history?")) return;
    setExpunging(true);
    try {
      await apiRequest(`/citizens/${citizen.id}/charges`, {
        method: "DELETE",
        token: currentUser()?.accessToken,
      });
      setSelected({ ...citizen, criminalHistory: [], charges: [] });
      await refetch();
    } finally {
      setExpunging(false);
    }
  };

  return (
    <Show
      when={selected()}
      fallback={
        <DumpList
          label="Profiles"
          loading={profiles.loading}
          search={search()}
          onSearch={setSearch}
          onAdd={hasPermission("citizens.create") ? createProfile : undefined}
          advancedValues={advanced()}
          onAdvancedApply={setAdvanced}
          advancedFields={profileAdvancedFields(tagOptions() ?? [])}
          entries={(profiles()?.items ?? []).map((citizen) => ({
            id: citizen.id,
            title: citizen.name,
            description: "",
            avatar: citizen.profilePicture ?? fallbackProfile,
            leftLabel: `ID: ${citizen.id}`,
            danger: citizen.isWanted,
            raw: citizen,
          }))}
          onSelect={(entry) => void select(entry.raw as CitizenSummary)}
        />
      }
    >
      {(citizen) => (
        <div class="flex h-full w-full gap-4">
          <div class="w-full">
            <DumpDetail
              label={`${citizen().name || "View Profile"} ( #${citizen().id || "new"} )`}
              onBack={() => {
                detailRequest += 1;
                setSelected(null);
              }}
              actions={
                <>
                  <Show when={hasPermission("citizens.update")}>
                    <button
                      class="flex h-7 w-7 cursor-pointer items-center justify-center bg-green-gradient transition-all duration-200 ease-in-out hover:drop-shadow-green-gradient disabled:opacity-40"
                      disabled={saving()}
                      aria-label="Save profile"
                      onClick={() => void save()}
                    >
                      <DumpIcon name="icon_save_docs" />
                    </button>
                  </Show>
                  <Show when={hasPermission("citizens.expunge") && citizen().id > 0}>
                    <button
                      class="flex h-7 w-7 cursor-pointer items-center justify-center bg-red-gradient transition-all duration-200 ease-in-out hover:drop-shadow-red-gradient disabled:opacity-40"
                      disabled={saving() || expunging()}
                      aria-label="Expunge criminal history"
                      title="Expunge"
                      onClick={() => void expunge()}
                    >
                      <DumpIcon name="icon_delete_docs" />
                    </button>
                  </Show>
                </>
              }
            >
              <div class="flex h-full min-h-0 flex-col gap-4 p-3 lg:flex-row">
                <Show when={loadingDetail()}>
                  <div class="absolute right-3 top-3 z-10 bg-white-11 px-2 py-1 text-xs text-white-55">
                    Loading profile...
                  </div>
                </Show>
                <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                <div class="flex gap-4">
                <ul class="flex min-h-11 w-full flex-col gap-3">
                  <DumpField
                    label="State ID"
                    value={citizen().id || stateId()}
                    disabled={citizen().id > 0}
                    type="number"
                    onInput={(value) => setStateId(Number(value) || 0)}
                  />
                  <DumpField label="Name" value={name()} onInput={setName} />
                  <DumpField
                    label="Profile Image URL"
                    value={profilePicture()}
                    onInput={setProfilePicture}
                  />
                  <DumpField
                    label="Date of Birth"
                    value={citizen().dateOfBirth}
                    disabled
                  />
                  <DumpField
                    label="Phone Number"
                    value={citizen().phoneNumber ?? ""}
                    disabled
                  />
                  <Show when={hasPermission("citizens.view.full")}>
                    <li class="flex min-h-10 items-center justify-between pl-4 leading-10">
                      <p>DNA</p>
                      <input
                        class="w-[70%] bg-[rgba(255,255,255,.11)] px-3 text-left text-white"
                        value={dna()}
                        placeholder="xxxx-xxxx-xxxx"
                        disabled={!hasPermission("citizens.update")}
                        onInput={(event) => setDna(event.currentTarget.value)}
                      />
                    </li>
                    <DumpField label="License Points" value={citizen().licensePoints ?? 0} disabled />
                  </Show>
                </ul>
                <div class="relative w-[15rem] shrink-0">
                  <img class="w-[15rem]" src={profilePicture() || fallbackProfile} alt="" />
                  <Show when={citizen().isWanted}>
                    <div class="absolute left-0 top-0 flex h-full w-full items-end justify-center pb-4">
                      <div class="rounded-sm bg-red-500 p-2"><p class="text-xs text-white">WANTED</p></div>
                    </div>
                  </Show>
                </div>
                </div>
                <div class="flex min-h-0 flex-1 flex-col gap-2 pl-4">
                    <p>Summary</p>
                    <RichTextEditor
                      value={summary()}
                      resetKey={citizen().id}
                      roomName={citizen().id > 0 ? `citizen-${citizen().id}` : undefined}
                      disabled={!hasPermission("citizens.update")}
                      onChange={setSummary}
                      onAutoSave={citizen().id > 0 ? save : undefined}
                    />
                </div>
                </div>
                <aside class="w-full max-w-full overflow-y-auto pr-1 lg:max-w-[30%]">
                  <ProfileRelations
                    citizen={citizen()}
                    onTagsUpdated={(tags) => setSelected({ ...citizen(), tags })}
                  />
                </aside>
              </div>
            </DumpDetail>
          </div>
        </div>
      )}
    </Show>
  );
}

function ProfileRelations(props: {
  citizen: CitizenSummary;
  onTagsUpdated: (tags: TagRecord[]) => void;
}) {
  const [warrants] = createResource(
    () => props.citizen.id > 0,
    () => apiRequest<Warrant[]>(`/citizens/${props.citizen.id}/warrants`, { token: currentUser()?.accessToken }).catch(() => []),
  );
  const [licenses] = createResource(
    () => props.citizen.id > 0,
    () => apiRequest<DirectoryLog[]>(`/citizens/${props.citizen.id}/licenses/logs`, { token: currentUser()?.accessToken }).catch(() => []),
  );
  const [activeLicenses, setActiveLicenses] = createSignal<CitizenLicense[]>(props.citizen.licenses ?? []);
  createEffect(() => {
    // The detail request replaces the selected summary with the dump-shaped
    // citizen payload. Keep the local license list in sync so a profile opened
    // from the list immediately shows its licenses without a second reload.
    setActiveLicenses(props.citizen.licenses ?? []);
  });
  const licenseTypes = [
    "Drivers License",
    "Weapons License",
    "State Bar License",
    "Business License",
    "Pilot License",
    "Skydiving License",
    "Hunting License",
    "Fishing License",
    "Alcohol License",
    "Federal Bar License",
  ];
  const addLicense = async (type: string) => {
    const result = await apiRequest<CitizenLicense[]>(`/citizens/${props.citizen.id}/licenses`, {
      method: "POST",
      token: currentUser()?.accessToken,
      body: { type },
    });
    setActiveLicenses(result);
  };
  const revokeLicense = async (license: CitizenLicense) => {
    const description = window.prompt("Reason for revoking this license", "") ?? "";
    await apiRequest(`/citizens/${props.citizen.id}/licenses`, {
      method: "DELETE",
      token: currentUser()?.accessToken,
      body: { type: license.type, description },
    });
    setActiveLicenses((current) => current.filter((item) => item.id !== license.id));
  };
  return (
    <>
      <li class="mt-3 flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
        <p class="roboto500">Warrants</p>
        <Show when={warrants()?.length} fallback={<p class="text-xs text-white-35">No active warrants.</p>}>
          <For each={warrants()}>{(warrant) => <p class="text-xs text-red-300">{warrant.title} · expires {new Date(warrant.warrantExpiry).toLocaleString()}</p>}</For>
        </Show>
      </li>
      <Show when={hasPermission("citizens.view.full")}>
        <li class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
          <p class="roboto500">License history</p>
          <Show when={licenses()?.length} fallback={<p class="text-xs text-white-35">No license events.</p>}>
            <For each={licenses()}>{(log) => <p class="text-xs text-white-55">{log.action} · {log.actor}</p>}</For>
          </Show>
        </li>
      </Show>
      <li class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
        <div class="flex items-center justify-between">
          <p class="roboto500">Licenses</p>
          <Show when={hasPermission("citizens.license.give")}>
            <select
              class="bg-gray-gradient px-2 py-1 text-xs"
              onChange={(event) => event.currentTarget.value && void addLicense(event.currentTarget.value)}
            >
              <option value="">Grant license</option>
              <For each={licenseTypes.filter((type) => !activeLicenses().some((license) => license.type === type && license.status === "active"))}>
                {(type) => <option value={type}>{type}</option>}
              </For>
            </select>
          </Show>
        </div>
        <Show when={activeLicenses().length} fallback={<p class="text-xs text-white-35">No licenses.</p>}>
          <For each={activeLicenses()}>
            {(license) => (
              <div class="flex items-center justify-between bg-gray-gradient px-2 py-1 text-xs">
                <span>{license.type} <span class="text-white-45">({license.status})</span></span>
                <Show when={hasPermission("citizens.license.remove") && license.status === "active"}>
                  <button class="text-red-300" onClick={() => void revokeLicense(license)}>Revoke</button>
                </Show>
              </div>
            )}
          </For>
        </Show>
      </li>
      <li class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
        <p class="roboto500">Vehicles</p>
        <Show when={props.citizen.vehicles?.length} fallback={<p class="text-xs text-white-35">No registered vehicles.</p>}>
          <For each={props.citizen.vehicles}>{(vehicle) => <p class="text-xs text-white-55">{vehicle.plate} · {vehicle.model}</p>}</For>
        </Show>
      </li>
      <li class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
        <p class="roboto500">Properties</p>
        <Show when={props.citizen.properties?.length} fallback={<p class="text-xs text-white-35">No properties.</p>}>
          <For each={props.citizen.properties}>{(property) => <p class="text-xs text-white-55">{property.name} · {property.isOwner ? "Owner" : "Keyholder"}</p>}</For>
        </Show>
      </li>
      <li class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
        <p class="roboto500">Criminal history</p>
        <Show when={props.citizen.criminalHistory?.length} fallback={<p class="text-xs text-white-35">No recorded charges.</p>}>
          <For each={props.citizen.criminalHistory}>{(charge) => <p class="text-xs text-white-55">{charge.name} ×{charge.count}</p>}</For>
        </Show>
      </li>
      <ProfileTags citizen={props.citizen} onSaved={props.onTagsUpdated} />
    </>
  );
}

function ProfileTags(props: {
  citizen: CitizenSummary;
  onSaved: (tags: TagRecord[]) => void;
}) {
  const [allTags] = createResource(() =>
    apiRequest<TagRecord[]>("/tags", { token: currentUser()?.accessToken }).catch(() => []),
  );
  const [selected, setSelected] = createSignal<number[]>(
    (props.citizen.tags ?? []).map((tag) => tag.id),
  );
  createEffect(() => {
    setSelected((props.citizen.tags ?? []).map((tag) => tag.id));
  });
  const [saving, setSaving] = createSignal(false);
  const save = async () => {
    setSaving(true);
    try {
      const tags = (allTags() ?? []).filter((tag) => selected().includes(tag.id));
      await apiRequest(`/citizens/${props.citizen.id}/tags`, {
        method: "PATCH",
        token: currentUser()?.accessToken,
        body: { tags: selected().map((tagId) => ({ tagId })) },
      });
      props.onSaved(tags);
    } finally {
      setSaving(false);
    }
  };
  return (
    <li class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
      <div class="flex items-center justify-between">
        <p class="roboto500">Tags</p>
        <button class="bg-green-gradient px-3 py-1 text-xs disabled:opacity-40" disabled={saving()} onClick={() => void save()}>
          Save Tags
        </button>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <For each={allTags() ?? []}>
          {(tag) => (
            <label class="flex cursor-pointer items-center gap-2 bg-gray-gradient px-2 py-2 text-xs">
              <input
                type="checkbox"
                checked={selected().includes(tag.id)}
                onChange={(event) =>
                  setSelected((current) =>
                    event.currentTarget.checked
                      ? [...new Set([...current, tag.id])]
                      : current.filter((id) => id !== tag.id),
                  )
                }
              />
              <span style={{ color: tag.color }}>{tag.name}</span>
            </label>
          )}
        </For>
      </div>
    </li>
  );
}

function profileAdvancedFields(tags: TagRecord[]) {
  return [
    {
      key: "tags",
      label: "Tags",
      type: "select" as const,
      options: tags.map((tag) => ({ value: String(tag.id), label: tag.name })),
    },
    { key: "createdFrom", label: "Date From", type: "datetime-local" as const },
    { key: "createdTo", label: "Date To", type: "datetime-local" as const },
  ];
}
